import { Stack } from 'component-library';
import { useEffect } from 'react';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { ItemizedGraphTypeRenderer } from '@/components/Charts/components/ItemizedGraphType';
import { HabitatIntactnessHistorical } from '@/components/Charts/HabitatIntactnessHistorical';
import { NUM_ITEMS_TYPE } from '@/components/Charts/hooks/usePlotsForItemizedGraph';
import { GraphFact } from '@/components/Charts/types';
import { GraphTileProps } from '@/components/IndicatorCard/GraphTile';
import { useSetIndicatorHasData } from '@/components/IndicatorCard/IndicatorContextProvider';
import { useTrendLabelFromOrganizationFact } from '@/hooks/useTrendLabelFromFact';
import {
  getBenchmarkForConservationFact,
  useConservationBenchmarksForOrganization,
} from '@/pages/shared/hooks/useBenchmarkForFact';
import {
  useOrganizationIndicatorGraphFact,
  useOrganizationIndicatorReportingFact,
} from '@/pages/shared/hooks/useOrganizationFact';
import { NoData } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/components/NoData';

export const HabitatIntactnessGraphTile = ({
  plotGroupId,
  graphType,
  zoom,
  setZoom,
  ...props
}: Partial<GraphTileProps>) => {
  const { setIndicatorGraphData } = useSetIndicatorHasData();

  const { [IndicatorName.habitat_intactness_total_historic_graph]: habitatIntactnessHistoric } =
    useOrganizationIndicatorGraphFact<GraphFact[]>(
      IndicatorName.habitat_intactness_total_historic_graph,
      null,
      undefined,
      plotGroupId,
    );

  const { [IndicatorName.habitat_intactness_total]: latestFact } = useOrganizationIndicatorReportingFact<number>(
    IndicatorName.habitat_intactness_total,
    null,
    undefined,
    plotGroupId,
  );

  const trendLabel = useTrendLabelFromOrganizationFact({
    fact: IndicatorName.habitat_intactness_total_previous_5y_pct_change,
  });

  const habitatIntactnessBenchmarkFact = IndicatorName.habitat_intactness_total_benchmark;

  const benchmarks = useConservationBenchmarksForOrganization(habitatIntactnessBenchmarkFact, plotGroupId ?? undefined);
  const benchmark = getBenchmarkForConservationFact({
    fact: latestFact,
    benchmarks,
  });

  const chartData = habitatIntactnessHistoric?.value;

  const noChartData = !chartData?.length;

  useEffect(() => {
    setIndicatorGraphData({ graphData: !noChartData });
  }, [noChartData, setIndicatorGraphData]);

  if (noChartData) {
    return <NoData />;
  }

  return (
    <Stack center data-testid='habitat-intactness-graph-tile'>
      <Stack spacing={2.5} className='w-full items-center justify-center px-1 py-6 lg:items-start lg:p-8 lg:pl-1'>
        <ItemizedGraphTypeRenderer
          graphType={graphType}
          indicator={GraphTileType.historic_habitat_intactness}
          plotRenderType={props?.plotRenderType ?? NUM_ITEMS_TYPE.TOP_PLOTS}
          plotGroupId={plotGroupId ?? undefined}
          zoom={zoom}
          setZoom={setZoom}
          defaultGraphType={
            <HabitatIntactnessHistorical
              data={chartData}
              benchmark={benchmark}
              benchmarks={benchmarks}
              trendLabel={trendLabel}
              plotGroupId={plotGroupId ?? undefined}
            />
          }
        />
      </Stack>
    </Stack>
  );
};
